import React from "react";
import { Factory, Layers, FileCheck2, Building2 } from "lucide-react";
import { useInView } from "../../hooks/use-in-view";

const pillars = [
  {
    icon: Factory,
    title: "Production Capacity",
    desc: "20,000+ MT annual output across five products. Glass-lined / FRP reactors, automated batching and a multi-line plant that scales with your contract."
  },
  {
    icon: Layers,
    title: "Multi-Grade Flexibility",
    desc: "Commercial, C.P., L.R. and A.R. — all four grades produced in-house. One supplier, one PO, one CoA standard for every purity you need."
  },
  {
    icon: FileCheck2,
    title: "Complete Documentation",
    desc: "Batch-specific CoA, MSDS, TDS, GST invoice, e-way bill and DG declaration travel with every consignment. Digital copies emailed in advance."
  },
  {
    icon: Building2,
    title: "Approved PSU Vendor",
    desc: "Empanelled with GeM and accepted on the vendor lists of leading PSUs, defence units and large infrastructure tenders across India."
  }
];

const Sustainability = () => {
  const [ref, inView] = useInView();
  return (
    <section id="advantage" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="text-[12px] uppercase tracking-[0.22em] font-semibold text-teal-700">The Manish Labchem Advantage</div>
          <h2 className="mt-3 font-display font-extrabold text-slate-900 text-[34px] lg:text-[46px] leading-[1.08]">
            <span className="text-gradient-anim">Built for</span> industrial buyers, tender-ready by design
          </h2>
          <p className="mt-5 text-slate-600 text-[16px] leading-relaxed">
            Procurement heads choose us not just for the chemistry, but for how predictably we deliver it. Forty years of plant operation has given us the production depth, paperwork discipline and vendor approvals that large B2B buyers and government tenders demand.
          </p>
          <div className="mt-7 grid grid-cols-3 gap-4">
            {[
              { v: "99.5%", l: "On-time dispatch rate" },
              { v: "24 hr", l: "Stocked-grade turnaround" },
              { v: "100%", l: "Batch traceability" }
            ].map((m) => (
              <div key={m.l} className="text-center p-3 rounded-xl bg-teal-50 border border-teal-100">
                <div className="font-display font-extrabold text-[24px] text-teal-800">{m.v}</div>
                <div className="text-[11.5px] text-slate-600 mt-1 leading-tight">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
        <div ref={ref} className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className={`reveal ${inView ? "is-visible" : ""} group rounded-2xl border border-slate-200 p-5 bg-white card-lift tilt-card`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="h-12 w-12 rounded-xl brand-band flex items-center justify-center shadow-md shadow-teal-900/20">
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="mt-4 font-display font-bold text-[18px] text-slate-900">{p.title}</h3>
                <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Sustainability;
